import React from 'react'
import type { CompanyFinancials } from '../../../types'
import {
  changePercentClassName,
  formatChangePercentArrow,
  formatKeyFigureAmount,
} from '../../../utils/financial'

interface CompanyKeyFiguresLineProps {
  financials: CompanyFinancials
}

function KeyFigurePart({
  amount,
  changePercent,
}: {
  amount: number
  changePercent: number | null
}) {
  const delta = formatChangePercentArrow(changePercent)

  return (
    <>
      <span>{formatKeyFigureAmount(amount)}</span>
      {delta && (
        <span className={`text-xs ${changePercentClassName(changePercent)}`}>{delta}</span>
      )}
    </>
  )
}

/**
 * Inline revenue / profit for the company status row: "1,23 mio. (↑3,4%) / 654.321 (↓15,2%)".
 */
const CompanyKeyFiguresLine: React.FC<CompanyKeyFiguresLineProps> = ({ financials }) => {
  const parts: React.ReactNode[] = []

  if (financials.revenue) {
    parts.push(
      <KeyFigurePart
        key="revenue"
        amount={financials.revenue.amount}
        changePercent={financials.revenue.changePercent}
      />
    )
  }

  if (financials.profit) {
    parts.push(
      <KeyFigurePart
        key="profit"
        amount={financials.profit.amount}
        changePercent={financials.profit.changePercent}
      />
    )
  }

  if (parts.length === 0) return null

  return (
    <span data-testid="company-key-figures-line" className="inline-flex flex-wrap items-baseline gap-x-1">
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <span className="text-ink-300" aria-hidden>
              /
            </span>
          )}
          {part}
        </React.Fragment>
      ))}
    </span>
  )
}

export default CompanyKeyFiguresLine
